import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { wormColors } from "core/src/core/constants.js";

import LiveRoomPreview, { type LiveRoomInfo } from "../../../shared/game-preview/LiveRoomPreview";
import GamePreview from "../../../shared/game-preview/GamePreview";
import { useEggBalance } from "../../../shared/shop/useEggBalance";
import { insigniaForValue, subProgress } from "../../../shared/rating/tiers";
import { RANKED_MODES, RANKED_PLACEMENT_GAMES, type RankedMode } from "../../../shared/rating/store";
import { RANKED } from "../../../shared/rating/ranked";
import { flagUrl, flagUrlSquare, isFlagCode } from "../../../shared/flags";
import { modeLook, ROUND_MODE_NAMES } from "../../../online/dynamic";
import type { CustomLobby } from "../../../online/net";
import { arenaLabel, mapValueOf, mapValueFor } from "../../setup/maps";
import { previewConfig, BOT_NAMES } from "../lobby";
import { PICK_COLORS, mmss, type Ratings, type RosterPlayer } from "./hooks";
import { Chev, ChevL, Globe, GoldenEgg, Keys, Lock, Plus, RankBadge, type BadgeLook } from "./icons";
import s from "./start.module.scss";

// ---------- the live window into Play Now ----------
// When no room is running, a local game stands in: six players on a Medium
// Rectangle (its straight walls leave the window with only a little zoom, so
// more of the game shows than a stadium would allow).
const FALLBACK_MAP = { value: mapValueFor("rectangle", "medium"), label: "Medium Rectangle" };
const FALLBACK_PLAYERS = 6;

// The stand-in game's cast: when no room is running the window still shows a
// game with people in it (names and flags like any room's), so the page never
// looks deserted. One cast per page load.
const STAND_IN_FLAGS = ["se", "de", "pl", "no", "nl", "fi", "dk", "gb", "fr", "es", "it", "us", "br", "cz", "at"];
function standInInfo(): LiveRoomInfo {
  const names = BOT_NAMES.slice().sort(() => Math.random() - 0.5).slice(0, FALLBACK_PLAYERS);
  const flags = STAND_IN_FLAGS.slice().sort(() => Math.random() - 0.5);
  const cfg = previewConfig(FALLBACK_MAP, FALLBACK_PLAYERS);
  const players = names.map((name, i) => ({ id: "s" + i, name, flag: flags[i], colorId: cfg.players[i].colorId }));
  const modes = ROUND_MODE_NAMES;
  const round = 2 + Math.floor(Math.random() * 5);
  return {
    room: "",
    setup: { dynamic: "playnow", mode: modes[Math.floor(Math.random() * modes.length)], mapDesc: null, sizeKey: "medium", shape: "Rectangle", round, scoreReset: false, set: 1, setRound: round, setRounds: 7 },
    players,
    leader: players[Math.floor(Math.random() * players.length)],
  };
}

// How far the live game is zoomed in: as little as hides the arena's wall, so as
// much of the game as possible shows. The arena's box first covers the window
// (W x H); `zoom` then scales it from the centre. A rectangle's wall is straight:
// covering already puts it at the window's edge, so a hair more hides it. A
// stadium's ends are half circles, so the window's corners must fit inside them.
const WALL = 1.05; // the wall's own thickness, and a little air
export function liveZoom(W: number, H: number, ar: number, stadium: boolean): number {
  if (!(W > 0 && H > 0) || !stadium) return WALL;
  const bw = Math.max(W, H * ar), bh = bw / ar; // the covering box
  const a = W / 2, b = H / 2;                   // the window's corner, from the centre
  // the corner is inside a stadium scaled by s when it is under the straight
  // part, or within the end's half circle
  const inside = (s: number) => {
    const r = (s * bh) / 2, straight = (s * (bw - bh)) / 2;
    return b <= r && (a <= straight || (a - straight) ** 2 + b ** 2 <= r ** 2);
  };
  let lo = 1, hi = 4;
  for (let i = 0; i < 24; i++) { const mid = (lo + hi) / 2; if (inside(mid)) hi = mid; else lo = mid; }
  return hi * WALL;
}

export function LiveWindow({ onInfo }: { onInfo: (info: LiveRoomInfo | null) => void }) {
  const [standIn] = useState(standInInfo);
  const [ar, setAr] = useState(2);
  // (the stand-in game is a rectangle)
  const [stadium, setStadium] = useState(false);
  const fitRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = fitRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setSize((cur) => (cur.w === r.width && cur.h === r.height ? cur : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fallback = useMemo(() => <GamePreview matchConfig={previewConfig(FALLBACK_MAP, FALLBACK_PLAYERS)} />, []);
  const report = useCallback((info: LiveRoomInfo | null) => {
    // the box takes the shape of the round's arena (they are 2:1 today)
    const d = info?.setup?.mapDesc;
    setAr(d && d.width > 0 && d.height > 0 ? d.width / d.height : 2);
    setStadium(!!info && !!info.setup && info.setup.shape === "Stadium");
    onInfo(info || standIn);
  }, [onInfo, standIn]);
  // (until the first report, and whenever no room is running: the stand-in)
  useEffect(() => { onInfo(standIn); }, [onInfo, standIn]);
  return (
    <div className={s.fit} ref={fitRef}>
      <div className={s.fitBox} style={{ "--ar": ar, "--zoom": liveZoom(size.w, size.h, ar, stadium) } as React.CSSProperties}>
        <LiveRoomPreview fallback={fallback} onInfo={report} />
      </div>
    </div>
  );
}

// Who is in the room, for the window's foot: their flags (one per country), how
// many are playing, and who leads.
export function LiveFoot({ info, className, lead }: { info: LiveRoomInfo | null; className?: string; lead?: React.ReactNode }) {
  if (!info || !info.players.length) return null;
  const flags = Array.from(new Set(info.players.map((p) => p.flag).filter((f): f is string => !!f && isFlagCode(f)))).slice(0, 8);
  return (
    <div className={`${s.liveFoot}${className ? " " + className : ""}`}>
      {lead}
      {flags.length > 0 && <span className={s.liveFlags}>{flags.map((f) => <img key={f} src={flagUrlSquare(f)} alt="" />)}</span>}
      <span><b>{info.players.length}</b> playing</span>
      {info.leader && (
        <span className={s.liveLead}>
          Leader {info.leader.flag && isFlagCode(info.leader.flag) ? <img src={flagUrlSquare(info.leader.flag)} alt="" /> : null}<b>{info.leader.name}</b>
        </span>
      )}
    </div>
  );
}

// "Round 3/7", or null when there is nothing to say yet.
export function liveLine(info: LiveRoomInfo | null): string | null {
  if (!info || !info.setup) return null;
  const r = info.setup;
  return r.setRound && r.setRounds ? `Round ${r.setRound}/${r.setRounds}` : `Round ${r.round}`;
}
// The round's game mode as a chip in the mode's own colour.
export function LiveModeChip({ info }: { info: LiveRoomInfo | null }) {
  if (!info || !info.setup) return null;
  const look = modeLook(info.setup.mode);
  return <span className={s.modeChip} style={{ "--mode": look.color } as React.CSSProperties}>{look.name}</span>;
}

// ---------- the golden Shop button: an egg and your Golden Eggs ----------
// Signed out (or the balance not known yet) it reads "Store".
export function ShopButton({ onClick, className }: { onClick: () => void; className?: string }) {
  const eggs = useEggBalance();
  return (
    <button type="button" className={`${s.shop}${className ? " " + className : ""}`} onClick={onClick} aria-label={eggs != null ? `Store, ${eggs} Golden Eggs` : "Store"} title="Store" data-store-eggs="">
      <GoldenEgg className={s.egg} />
      {eggs != null ? <b>{eggs}</b> : <span>Store</span>}
    </button>
  );
}

// ---------- Ranked ----------
const NUMERAL_PIPS: Record<string, number> = { I: 1, II: 2, III: 3 };

interface RankView { look: BadgeLook; color: string | null; tier: string | null; rating: number | null; played: number | null }
export function rankView(mode: RankedMode, ratings: Ratings, signedIn: boolean): RankView {
  if (!signedIn) return { look: { kind: "locked" }, color: null, tier: null, rating: null, played: null };
  if (ratings === undefined) return { look: { kind: "blank" }, color: null, tier: null, rating: null, played: null };
  const row = ratings ? ratings[mode] : null;
  if (row && row.placed) {
    const v = row.displayRating;
    const ins = insigniaForValue(v);
    const prog = subProgress(v);
    return {
      look: { kind: "ranked", color: ins.tier.color, pct: prog ? prog.pct * 100 : 100, pips: ins.numeral ? NUMERAL_PIPS[ins.numeral] : 0, value: v },
      color: ins.tier.color, tier: ins.label, rating: v, played: null,
    };
  }
  const played = row ? Math.min(row.games, RANKED_PLACEMENT_GAMES) : 0;
  return { look: { kind: "placing", pct: (played / RANKED_PLACEMENT_GAMES) * 100 }, color: null, tier: null, rating: null, played };
}

// The profile chip's second line: your best ladder ("Gold II · 1371").
export function ratingLine(ratings: Ratings, signedIn: boolean): { text: string; color: string | null } {
  if (!signedIn) return { text: "Not signed in", color: null };
  const placed = RANKED_MODES.map((m) => rankView(m, ratings, true)).filter((v) => v.rating != null).sort((a, b) => b.rating! - a.rating!)[0];
  if (!placed) return { text: ratings === undefined ? "" : "Unranked", color: null };
  return { text: `${placed.tier} · ${placed.rating}`, color: placed.color };
}

// The two rank buttons. A click opens that ladder's page; signed out it asks
// you to sign in (the page decides how, via onOpen).
export function RankButtons({ ratings, signedIn, onOpen, signInRow, onLogin, sideBySide }: {
  ratings: Ratings;
  signedIn: boolean;
  onOpen: (mode: RankedMode) => void;
  signInRow?: boolean; // the landscape panel has no box header to hold "Sign in"
  onLogin?: () => void;
  sideBySide?: boolean; // the two in one row (a profile's overview), not one under the other
}) {
  return (
    <div className={`${s.rkList} ${sideBySide ? s.rkSide : ""}`}>
      {RANKED_MODES.map((mode) => {
        const v = rankView(mode, ratings, signedIn);
        return (
          <button key={mode} type="button" className={`${s.rk} ${v.color ? "" : s.rkNone}`} style={v.color ? ({ "--tier": v.color } as React.CSSProperties) : undefined} onClick={() => onOpen(mode)} aria-label={`Ranked ${RANKED[mode].title}`} data-sound="confirm">
            <RankBadge look={v.look} className={s.badge} inner={36} />
            <span className={s.rkBody}>
              <span className={s.rkHead}>
                <b>{RANKED[mode].title}</b>
                {v.tier ? <em>{v.tier}</em> : v.played != null ? <em className={s.rkPlacing}>Placements</em> : null}
                {v.rating != null
                  ? <strong>{v.rating}</strong>
                  : v.played != null
                    ? <strong>{v.played} / {RANKED_PLACEMENT_GAMES}</strong>
                    : !signedIn ? <span className={s.rkUn}>Unranked</span> : null}
              </span>
              {/* The way to the next step: a bar filling within the tier, or one
                  piece per placement match. */}
              {v.rating != null ? (
                <span className={s.rkBar}><i style={{ transform: `scaleX(${v.look.kind === "ranked" ? Math.max(0, Math.min(1, v.look.pct / 100)) : 0})` }} /></span>
              ) : v.played != null ? (
                <span className={`${s.rkBar} ${s.rkSeg}`} aria-label={`${v.played} of ${RANKED_PLACEMENT_GAMES} placement matches played`}>
                  {Array.from({ length: RANKED_PLACEMENT_GAMES }, (_, i) => <i key={i} className={i < v.played! ? s.rkOn : ""} />)}
                </span>
              ) : <span className={s.rkBar} />}
            </span>
          </button>
        );
      })}
      {!signedIn && signInRow && (
        <div className={s.rkSign}>
          <span>Sign in to play ranked</span>
          <button type="button" className={s.signIn} onClick={onLogin}>Sign in</button>
        </div>
      )}
    </div>
  );
}

// ---------- Game Rooms (the PC box and the landscape panel) ----------
// Join by code, the open public rooms, and Create room (public, private or a
// local game on this device).
const cleanCode = (c: string) => c.trim().toUpperCase().slice(0, 8); // as the Join page

export function RoomsBody({ rooms, onLocal }: { rooms: CustomLobby[]; onLocal: () => void }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [choosing, setChoosing] = useState(false);
  const join = (c: string) => { const clean = cleanCode(c); if (clean) navigate(`/lobby/${clean}`); };
  // The room opens the moment its page does, with whoever opens it as the host.
  // Straight into a new room on the Classic rules (changed in the room itself).
  const create = (pub: boolean) => navigate(`/join?create=${pub ? "public" : "private"}`);

  if (choosing) {
    return (
      <div className={s.choose}>
        <div className={s.madeHead}><small>Create a room</small><button type="button" className={s.back} onClick={() => setChoosing(false)}><ChevL />Back</button></div>
        <div className={s.choice}>
          <button type="button" className={s.opt} onClick={() => create(true)}>
            <span className={s.optIcon}><Globe /></span>
            <span className={s.optTxt}><b>Public</b><small>Listed in open rooms, anyone can join</small></span>
            <Chev className={s.chev} />
          </button>
          <button type="button" className={s.opt} onClick={() => create(false)}>
            <span className={s.optIcon}><Lock /></span>
            <span className={s.optTxt}><b>Private</b><small>Only people with the code or link</small></span>
            <Chev className={s.chev} />
          </button>
          <button type="button" className={s.opt} onClick={onLocal}>
            <span className={s.optIcon}><Keys /></span>
            <span className={s.optTxt}><b>Local</b><small>Everyone plays on this device</small></span>
            <Chev className={s.chev} />
          </button>
        </div>
      </div>
    );
  }
  return (<>
    <div className={s.label}>Join a room</div>
    <form className={s.codeRow} onSubmit={(e) => { e.preventDefault(); join(code); }}>
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))} placeholder="CODE" aria-label="Room code" autoComplete="off" autoCapitalize="characters" spellCheck={false} />
      <button type="submit" disabled={!code.trim()}>Join</button>
    </form>
    <div className={s.label}>Open rooms</div>
    <div className={s.rooms}>
      {rooms.length === 0 && <div className={s.noRooms}>No open rooms right now</div>}
      {rooms.map((r) => (
        // The whole card is the button: raised at rest, dips when pressed.
        <button key={r.code} type="button" className={s.roomCard} onClick={() => join(r.code)} aria-label={`Join ${r.hostName || "this"} room`}>
          <span className={s.roomText}>
            <b>{r.hostName ? `${r.hostName}'s room` : "Open room"}</b>
            <small>{r.map ? arenaLabel(mapValueOf(r.map)) + " · " : ""}{r.players}/{r.maxPlayers}</small>
          </span>
          <Chev className={s.chev} />
        </button>
      ))}
    </div>
    <button type="button" className={s.createBtn} onClick={() => setChoosing(true)}><Plus />Create room</button>
  </>);
}

// ---------- colour swatches ----------
// A colour another player on this device already has is greyed out.
// The colour swatches. With `row`, only that many show (yours among them) and a
// last button opens the rest underneath.
export function Swatches({ player, players, onPick, row }: { player: RosterPlayer; players: RosterPlayer[]; onPick: (colorId: string) => void; row?: number }) {
  const [more, setMore] = useState(false);
  // a colour from outside the list (one the server handed out) goes last, so the
  // others never move
  const ids = PICK_COLORS.includes(player.colorId) ? PICK_COLORS : [...PICK_COLORS, player.colorId];
  const swatch = (id: string) => (
    <button key={id} type="button" className={`${s.sw} ${player.colorId === id ? s.swOn : ""}`} style={{ background: (wormColors as any)[id] }} disabled={players.some((p) => p.slot !== player.slot && p.colorId === id)} onClick={() => onPick(id)} aria-label={id} aria-pressed={player.colorId === id} />
  );
  if (!row || ids.length <= row + 1) return <div className={s.swatches}>{ids.map(swatch)}</div>;
  // the first row keeps your colour in it, so the pick always shows: in its last
  // place, the others stay put
  const first = ids.slice(0, row).includes(player.colorId) ? ids.slice(0, row) : [...ids.slice(0, row - 1), player.colorId];
  const rest = ids.filter((id) => !first.includes(id));
  return (
    <div className={s.swRows}>
      <div className={`${s.swatches} ${s.swOne}`}>
        {first.map(swatch)}
        <button type="button" className={`${s.sw} ${s.swMore} ${more ? s.swMoreOn : ""}`} onClick={() => setMore((v) => !v)} aria-expanded={more} aria-label="More colours">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
        </button>
      </div>
      {more && <div className={`${s.swatches} ${s.swRest}`}>{rest.map(swatch)}</div>}
    </div>
  );
}

export const avatarUrl = (flag: string) => flagUrl(flag);

// ---------- Royale ----------
// The clock with each character in a cell of its own width, so nothing beside
// it shifts as the digits change (the display face has no fixed-width figures).
// What the Royale is, in a line and three points (the "i" below says it).
function ROYALE_WORDS() {
  return (
    <>
      <b>30 players. Last worm standing!</b>
      <ul>
        <li>Every 3 rounds, the bottom of the table is out (7, then 6, then 5...).</li>
        <li>No need to win, just stay off the bottom.</li>
        <li>The final 2 players battle it out in the golden Royale Arena.</li>
      </ul>
      <span className={s.royInfoMut}>A new Royale starts every half hour.</span>
    </>
  );
}

// The Royale in a few words: a quiet "i" left of its clock. Hover (or focus)
// shows it on a PC, a tap on a phone; it never opens the card it sits on. The
// words sit in a portal, so the card's rounded, clipped edges don't cut them.
export function RoyaleInfo({ className }: { className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const mouse = useRef(false);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right - 10) });
  };
  const hide = () => setAt(null);
  useEffect(() => {
    if (!at) return;
    const away = (e: Event) => { if (!ref.current?.contains(e.target as Node)) hide(); };
    document.addEventListener("pointerdown", away);
    window.addEventListener("scroll", hide, true);
    return () => { document.removeEventListener("pointerdown", away); window.removeEventListener("scroll", hide, true); };
  }, [at]);
  const stop = (e: React.SyntheticEvent) => { e.stopPropagation(); };
  return (
    <span
      ref={ref}
      className={`${s.royInfo} ${className || ""}`}
      role="button" tabIndex={0} aria-label="What is the Royale?" aria-expanded={!!at}
      // (no click sound: it only shows words)
      data-sound="none"
      // (hover only for a mouse: a tap sends a hover too, then the click would shut it)
      onPointerEnter={(e) => { if (e.pointerType === "mouse") show(); }} onPointerLeave={(e) => { if (e.pointerType === "mouse") hide(); }} onBlur={hide}
      // a mouse has it open from the hover already: a click keeps it; a tap toggles it
      onClick={(e) => { stop(e); if (mouse.current) show(); else if (at) hide(); else show(); }}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { stop(e); e.preventDefault(); if (at) hide(); else show(); } }}
      onPointerDown={(e) => { stop(e); mouse.current = e.pointerType === "mouse"; }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 11v5.5M12 7.6v.1" /></svg>
      {/* the same words, always in the page for screen readers (the panel only exists while open) */}
      <span className={s.srOnly}><ROYALE_WORDS /></span>
      {at && createPortal(
        <span className={s.royInfoPop} style={{ top: at.top, right: at.right }} role="tooltip">
          <ROYALE_WORDS />
        </span>,
        document.body,
      )}
    </span>
  );
}

export function RoyClock({ secs }: { secs: number | null }) {
  return <b className={s.royDigits}>{mmss(secs).split("").map((ch, k) => <span key={k} className={ch === ":" ? s.royColon : undefined}>{ch}</span>)}</b>;
}
// A seat for each of the 30 players, the taken ones gold.
export function RoySeats({ pct }: { pct: number }) {
  const taken = Math.round((pct / 100) * 30);
  return <span className={s.roySeats} aria-hidden="true">{Array.from({ length: 30 }, (_, k) => <i key={k} className={k < taken ? s.roySeatOn : undefined} />)}</span>;
}
